const ILLEGAL = /[/\\:*?"<>|\u0000-\u001f\u007f]+/g;
const RESERVED = /^(con|prn|aux|nul|com\d|lpt\d)$/i;
const EXTENSION = /\.[^.\s]{1,10}$/;

/**
 * A file name every OS accepts, built from a title a person typed:
 * „Angebot 12/2026: Müller?“ → „Angebot 12-2026- Müller“.
 *
 * Path separators, `:*?"<>|` and control characters become `-`, whitespace
 * collapses, leading and trailing dots and spaces go, and Windows device
 * names (`CON`, `LPT1.txt`, `nul.tar.gz`, …) get a `_` prefix. A name longer
 * than `maxLength` characters is cut before its extension, so `.pdf` survives
 * — unless the extension alone would not leave room for a name. Umlauts and
 * other letters stay. An empty result gives `fallback`, cleaned the same way.
 */
export function safeFileName(
  name: string | null | undefined,
  fallback = 'download',
  maxLength = 120
): string {
  const limit = Math.max(1, Math.floor(maxLength));
  return clean(name, limit) || clean(fallback, limit) || 'download'.slice(0, limit);
}

function clean(name: string | null | undefined, limit: number): string {
  const text = trimEdges(
    String(name ?? '')
      .normalize('NFC')
      .replace(ILLEGAL, '-')
      .replace(/\s+/g, ' ')
  );
  if (!text) return '';

  let extension = EXTENSION.exec(text)?.[0] ?? '';
  // Keep the extension only when a name of at least a few characters still fits.
  if (extension && Array.from(extension).length > limit - 3) extension = '';
  let base = extension ? text.slice(0, -extension.length) : text;

  const room = limit - Array.from(extension).length;
  const chars = Array.from(base);
  if (chars.length > room) base = trimEdges(chars.slice(0, room).join(''));
  if (!base) base = '_';
  // Windows reserves the device name before the first dot, whatever follows.
  if (RESERVED.test(base.split('.')[0]!.trim())) {
    base = `_${base}`;
    const prefixed = Array.from(base);
    if (prefixed.length > room) base = prefixed.slice(0, Math.max(1, room)).join('');
  }

  const result = trimEdges(base + extension);
  const all = Array.from(result);
  return all.length > limit ? trimEdges(all.slice(0, limit).join('')) : result;
}

function trimEdges(text: string): string {
  return text.replace(/^[\s.]+|[\s.]+$/g, '');
}
